import React, { useState, useRef } from 'react'

const fontOptions = [
  { value: 'Inter, system-ui, sans-serif', label: 'Inter', category: 'Sans Serif' },
  { value: 'Arial, sans-serif', label: 'Arial', category: 'Sans Serif' },
  { value: 'Helvetica, Arial, sans-serif', label: 'Helvetica', category: 'Sans Serif' },
  { value: 'Verdana, sans-serif', label: 'Verdana', category: 'Sans Serif' },
  { value: 'Trebuchet MS, sans-serif', label: 'Trebuchet MS', category: 'Sans Serif' },
  { value: 'Georgia, serif', label: 'Georgia', category: 'Serif' },
  { value: 'Times New Roman, serif', label: 'Times New Roman', category: 'Serif' },
  { value: 'Palatino Linotype, serif', label: 'Palatino', category: 'Serif' },
  { value: 'Garamond, serif', label: 'Garamond', category: 'Serif' },
  { value: 'Courier New, monospace', label: 'Courier New', category: 'Monospace' }
]

export const FontPicker = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [dropdownStyle, setDropdownStyle] = useState({})
  const buttonRef = useRef(null)
  const currentFont = fontOptions.find(f => f.value === value) || fontOptions[0]

  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      const dropdownHeight = 300
      const spaceBelow = window.innerHeight - rect.bottom - 8
      const openUpward = spaceBelow < dropdownHeight && rect.top > spaceBelow

      let left = rect.left
      if (left + rect.width > window.innerWidth - 16) {
        left = window.innerWidth - rect.width - 16
      }

      if (openUpward) {
        setDropdownStyle({
          position: 'fixed',
          bottom: `${window.innerHeight - rect.top + 4}px`,
          left: `${left}px`,
          width: `${rect.width}px`
        })
      } else {
        setDropdownStyle({
          position: 'fixed',
          top: `${rect.bottom + 4}px`,
          left: `${left}px`,
          width: `${rect.width}px`
        })
      }
    }
    setIsOpen(!isOpen)
  }

  return (
    <div style={{ marginBottom: '16px' }}>
      <label style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '6px' }}>Font</label>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        style={{
          width: '100%',
          padding: '10px 12px',
          border: '1px solid #e5e5e5',
          borderRadius: '8px',
          backgroundColor: 'white',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontFamily: value || 'Inter, system-ui, sans-serif',
          fontSize: '14px',
          color: '#1a1a1a'
        }}
      >
        <span>{currentFont.label}</span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2">
          <polyline points={isOpen ? "18 15 12 9 6 15" : "6 9 12 15 18 9"}/>
        </svg>
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            onClick={() => setIsOpen(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 999 }}
          />
          <div
            style={{
              ...dropdownStyle,
              backgroundColor: 'white',
              borderRadius: '10px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.16)',
              zIndex: 1000,
              maxHeight: '280px',
              overflowY: 'auto'
            }}
          >
            {['Sans Serif', 'Serif', 'Monospace'].map(category => (
              <div key={category}>
                <div style={{
                  padding: '8px 12px',
                  fontSize: '10px',
                  fontWeight: '600',
                  color: '#9ca3af',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  backgroundColor: '#f9fafb',
                  position: 'sticky',
                  top: 0
                }}>
                  {category}
                </div>
                {fontOptions.filter(f => f.category === category).map(font => (
                  <button
                    key={font.value}
                    onClick={() => {
                      onChange(font.value)
                      setIsOpen(false)
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: 'none',
                      backgroundColor: value === font.value ? '#f0fdfa' : 'transparent',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontFamily: font.value,
                      fontSize: '14px',
                      color: value === font.value ? '#007EA0' : '#374151',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <span>{font.label}</span>
                    {value === font.value && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#007EA0" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

export const CompactFontPicker = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [dropdownStyle, setDropdownStyle] = useState({})
  const buttonRef = useRef(null)
  const currentFont = fontOptions.find(f => f.value === value) || fontOptions[0]

  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      const dropdownHeight = 220
      const spaceBelow = window.innerHeight - rect.bottom - 8
      const openUpward = spaceBelow < dropdownHeight && rect.top > spaceBelow

      if (openUpward) {
        setDropdownStyle({
          position: 'fixed',
          bottom: `${window.innerHeight - rect.top + 2}px`,
          left: `${rect.left}px`,
          width: `${rect.width}px`
        })
      } else {
        setDropdownStyle({
          position: 'fixed',
          top: `${rect.bottom + 2}px`,
          left: `${rect.left}px`,
          width: `${rect.width}px`
        })
      }
    }
    setIsOpen(!isOpen)
  }

  return (
    <div style={{ marginBottom: '8px' }}>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        style={{
          width: '100%',
          padding: '8px 10px',
          border: '1px solid #e5e5e5',
          borderRadius: '6px',
          backgroundColor: 'white',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: '12px',
          color: '#374151'
        }}
      >
        <span style={{ fontFamily: value }}>{currentFont.label}</span>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2">
          <polyline points={isOpen ? "18 15 12 9 6 15" : "6 9 12 15 18 9"}/>
        </svg>
      </button>

      {isOpen && (
        <>
          <div
            onClick={() => setIsOpen(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 999 }}
          />
          <div
            style={{
              ...dropdownStyle,
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
              zIndex: 1000,
              maxHeight: '200px',
              overflowY: 'auto'
            }}
          >
            {fontOptions.map(font => (
              <button
                key={font.value}
                onClick={() => {
                  onChange(font.value)
                  setIsOpen(false)
                }}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: 'none',
                  backgroundColor: value === font.value ? '#f0fdfa' : 'transparent',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontFamily: font.value,
                  fontSize: '12px',
                  color: value === font.value ? '#007EA0' : '#374151'
                }}
              >
                {font.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
